/**
 * The application shell: rail, bar, and one scrolling content pane.
 *
 * It ships with B1 because B1 is the first screen that needs it, and it ships as a component
 * rather than as B1's own layout because every later screen composes with the same two pieces
 * (CLAUDE.md retro rule 8 — a cross-cutting object lands once, the first time it appears).
 *
 * The frame draws chrome unconditionally. **Whether chrome may be drawn at all is not its
 * decision** — an unclaimed home workspace is shown none (MCK-20260812-009 §E), and that is a
 * property of the session, so it is answered one layer up in `features/projects/AppChrome`.
 * A shell that could silently render itself away would put a security-shaped rule inside a
 * layout component, where nobody would test it.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';

import styles from './Chrome.module.css';
import { LeftRail } from './LeftRail';
import type { SwitchableWorkspace } from './AvatarMenu';
import { TopBar } from './TopBar';

export interface AppFrameProps {
  readonly workspaceInitial: string;
  /** Whether the rail shows the studio-settings entry. See `LeftRail`. */
  readonly canAdministerStudio: boolean;
  readonly userLabel: string;
  readonly userInitials: string;
  /** Every company the caller works for, for the company section of the avatar menu. */
  readonly workspaces: readonly SwitchableWorkspace[];
  readonly activeWorkspaceId: string | null;
  /** Ends the session. Owned by `AppChrome`, which is where the session queries live. */
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
  readonly crumbs: readonly string[];
  /**
   * The project tab row, when there is a project. Passed in rather than built here because
   * *"the tabs row hides when outside a project context"* (chat1.md §4) is a fact about the
   * screen, and the frame is drawn by workspace-level screens too.
   */
  readonly tabs?: ReactNode;
  /**
   * The phase bar, drawn **above** the tabs (`KLQ-20260827-002`). Passed in for the reason
   * `tabs` is: whether there is a project context is a fact about the screen, and this frame
   * is drawn by workspace-level screens too. Independent of `tabs` rather than folded into
   * it — the project-shape wizard draws the shell with no tab lit, and a phase bar that could
   * only exist beside a tab row would vanish there.
   */
  readonly phaseBar?: ReactNode;
  /**
   * A band drawn at the top of the content pane, above whatever screen is current — the
   * re-acceptance prompt is its only occupant today (`KLQ-20260814-007` §C).
   *
   * Passed in rather than built, for `tabs`' reason: whether there is anything to say is a
   * property of the session, and the frame is a layout component.
   *
   * **Inside the pane and not above it**, which is the one thing to preserve when a second
   * occupant arrives. The pane publishes its own distance from the viewport top as
   * `--app-scroll-top` for the sticky rails below it, and the two rows above it are fixed
   * heights this file names classes for. A variable-height row above the pane would make that
   * number a guess, and a guessed offset is a rail that stands in the wrong place on exactly
   * the screens that have one. Drawn inside, the pane's geometry is unchanged and the band
   * simply scrolls with the content — which is also what keeps it a notice rather than
   * something a person has to get past.
   */
  readonly notice?: ReactNode;
  readonly children: ReactNode;
}

export function AppFrame({
  workspaceInitial,
  canAdministerStudio,
  userLabel,
  userInitials,
  workspaces,
  activeWorkspaceId,
  onSignOut,
  signingOut,
  crumbs,
  tabs,
  phaseBar,
  notice,
  children,
}: AppFrameProps) {
  /**
   * Whether the navigation drawer is open. **A phone's state only** — above 720px the rail is
   * a permanent column and the stylesheet ignores this.
   *
   * Held here rather than in `LeftRail` because two components read it: the rail opens and the
   * top bar's button is what opens it. State in the rail would mean a callback going up and a
   * prop coming back down through the same parent.
   */
  const [navOpen, setNavOpen] = useState(false);
  const path = useRouterState({ select: (state) => state.location.pathname });

  // Following a link in the drawer navigates *and* leaves the drawer standing over the screen
  // it navigated to. Closing on the path is the fix rather than an `onClick` on every item:
  // one rule, and it covers a link added later by somebody who never read this file.
  useEffect(() => {
    setNavOpen(false);
  }, [path]);

  // Escape closes it, as it closes every other overlay in the product.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  return (
    <div className={styles.frame}>
      <LeftRail
        open={navOpen}
        onClose={() => {
          setNavOpen(false);
        }}
        workspaceInitial={workspaceInitial}
        canAdministerStudio={canAdministerStudio}
        userLabel={userLabel}
        userInitials={userInitials}
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        onSignOut={onSignOut}
        signingOut={signingOut}
      />
      <div className={styles.column}>
        <TopBar
          crumbs={crumbs}
          onOpenNav={() => {
            setNavOpen(true);
          }}
        />
        {phaseBar}
        {tabs}
        {/* The pane publishes its own distance from the top of the viewport as a custom
            property, and the two rows above it are what change it — see `Chrome.module.css`. */}
        <main
          className={[
            styles.content,
            tabs === undefined ? '' : styles.contentWithTabs,
            phaseBar === undefined ? '' : styles.contentWithPhaseBar,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {notice}
          {children}
        </main>
      </div>
    </div>
  );
}
